<script setup lang="ts">
import { ref, computed } from 'vue';
import { useUxData } from '@/stores/ux-data';
import ElementGrid from '@/components/data/grid/ElementGrid.vue';
import { useDoubleClick } from '@/composables/useDoubleClick';
import { useOffcanvas } from '@/composables/useOffcanvas';
import type { WorldInterface } from '@/types/worlds';
import type { SettingElementInterface } from '@/types/items';
import type { ColumnConfig, PaginationMetadata, PaginationLinks } from '@/types/browser';

const props = withDefaults(
  defineProps<{
    placement?: 'start' | 'bottom' | 'end';
    title: string;
    dataItems: WorldInterface[];
    columns: ColumnConfig[];
    selectedElement?: WorldInterface | null;
    loading?: boolean;
    metadata?: PaginationMetadata;
    links?: PaginationLinks;
    canCreate: boolean;
  }>(),
  {
    placement: 'end',
    selectedElement: null,
    loading: false,
    metadata: () => ({ total: null, start: null, end: null }),
    links: () => ({ next: null, previous: null, current: null }),
  },
);

const emit = defineEmits<{
  select: [world: WorldInterface];
  confirm: [world: WorldInterface];
  search: [event: any];
  next: [];
  previous: [];
  open: [];
  close: [];
  createCampaign: [];
}>();

const ux = useUxData();
const browserOffcanvas = ref<HTMLElement | null>(null);
// The browser starts fresh each time it opens: its host clears the keyword (useWorldBrowser) and
// asks for an unfiltered first page. The grid's search box is not bound to that keyword, so it is
// emptied here, or it would show the last text typed over that unfiltered page.
const grid = ref<InstanceType<typeof ElementGrid> | null>(null);
const { show, hide } = useOffcanvas(browserOffcanvas, {
  onOpen: () => {
    grid.value?.resetSearch();
    emit('open');
  },
  onClose: () => emit('close'),
});

// Single click selects (highlight), double click confirms — timer-based detection
// on the grid's repeated "select" events (the grid emits "select" on each click).
const { onClick: onRowClick } = useDoubleClick(
  (world: WorldInterface) => emit('confirm', world),
  { onSingleClick: (world: WorldInterface) => emit('select', world) },
);

// Suppress the detail card entirely when the selected world has no summary.
const detailElement = computed<WorldInterface | null>(() =>
  props.selectedElement?.summary ? props.selectedElement : null,
);

// The grid hands back its shared element type. Every row in this grid is a world, so the row is
// re-typed where it is used: an assertion, not a conversion.
const onSelect = (world: SettingElementInterface) => onRowClick(world as WorldInterface);
const onConfirmClick = (world: WorldInterface) => emit('confirm', world);
const onSearch = (event: any) => emit('search', event);
const onNext = () => emit('next');
const onPrevious = () => emit('previous');
const onCreate = () => emit('createCampaign');

defineExpose({ show, hide });
</script>

<template>
  <div
    ref="browserOffcanvas"
    class="world-browser browser-offcanvas offcanvas"
    :class="`offcanvas-${placement}`"
    tabindex="-1"
  >
    <div class="offcanvas-header">
      <h5 class="offcanvas-title">{{ title }}</h5>
      <button
        v-if="canCreate"
        type="button"
        class="btn btn-sm btn-outline-secondary ms-auto me-2 create-campaign"
        @click="onCreate"
      >
        <span class="fa-light fa-plus"></span>
        {{ (ux.labels as any)?.guidedSetup?.worldSetup ?? 'Create Campaign' }}
      </button>
      <button
        type="button"
        class="btn-close"
        :aria-label="(ux.labels as any)?.actions?.close ?? 'Close'"
        data-bs-dismiss="offcanvas"
      ></button>
    </div>
    <div class="offcanvas-body">
      <ElementGrid
        ref="grid"
        :data-items="dataItems"
        :columns="columns"
        :selected-element="detailElement"
        :loading="loading"
        :metadata="metadata"
        :links="links"
        :show-details-panel="true"
        :search-placeholder="(ux.labels as any)?.placeholders?.search ?? 'Search...'"
        @select="onSelect"
        @search="onSearch"
        @next="onNext"
        @previous="onPrevious"
      >
        <template #empty>
          <div class="py-4 text-center">
            <p class="mb-2">
              {{ (ux.labels as any)?.guidedSetup?.character?.noWorlds ?? 'You have no campaign settings yet.' }}
            </p>
            <button
              v-if="canCreate"
              type="button"
              class="btn btn-primary create-campaign-empty"
              @click="onCreate"
            >
              <span class="fa-light fa-plus"></span>
              {{ (ux.labels as any)?.guidedSetup?.worldSetup ?? 'Create Campaign' }}
            </button>
          </div>
        </template>

        <template #details="{ element }">
          <template v-if="(element as WorldInterface).summary">
            <div class="small mb-3" v-html="(element as WorldInterface).summary"></div>
            <button
              type="button"
              class="btn btn-primary w-100 use-setting"
              @click="onConfirmClick(element as WorldInterface)"
            >
              {{ (ux.labels as any)?.actions?.add ?? 'Use this setting' }}
            </button>
          </template>
        </template>
      </ElementGrid>
    </div>
  </div>
</template>
